import { getStructure, zoneNpcs } from "@two-crowns/data";
import type { Biome, Zone } from "@two-crowns/data/schema";
import { distToPolyline } from "./geometry";
import { seededRng } from "./rng";
import { regionReach, waterDistance } from "./terrain";

export { distToPolyline };

export interface Placement {
  type: string;
  x: number;
  z: number;
  /** Radians around Y (Babylon convention: local +z turns toward (sin, cos)). */
  rot: number;
  scale: number;
  /** Stable per-instance number for visual variation. */
  variant: number;
  /** Whose banner it flies, when not the town's. */
  nation?: "human" | "orc";
}

export interface ZoneLayout {
  /** Placed buildings and props (town data). */
  structures: Placement[];
  /** Scattered plants, rocks and ground cover. */
  nature: Placement[];
  /** Palisade stakes. */
  posts: { x: number; z: number }[];
  gates: { x: number; z: number; rot: number }[];
}

const DEG = Math.PI / 180;

/** What grows where: weights per biome. Presentation only (no gameplay effect). */
const BIOME_MIX: Record<Biome, [string, number][]> = {
  forest: [
    ["tree_oak", 40],
    ["tree_pine", 18],
    ["tree_birch", 10],
    ["bush", 12],
    ["stump", 4],
    ["log", 4],
    ["mushroom", 6],
    ["rock", 4],
    ["fern", 12],
  ],
  pine_forest: [
    ["tree_pine", 62],
    ["tree_dead", 4],
    ["rock", 8],
    ["log", 5],
    ["stump", 4],
    ["fern", 10],
    ["mushroom", 4],
    ["boulder", 3],
  ],
  birch_grove: [
    ["tree_birch", 50],
    ["tree_oak", 10],
    ["flowers", 20],
    ["bush", 10],
    ["fern", 10],
  ],
  meadow: [
    ["flowers", 38],
    ["grass", 34],
    ["bush", 10],
    ["tree_oak", 6],
    ["tree_birch", 4],
    ["rock", 6],
    ["boulder", 2],
  ],
  farmland: [
    ["grass", 55],
    ["flowers", 25],
    ["bush", 8],
    ["haystack", 4],
    ["tree_oak", 8],
  ],
  marsh: [
    ["reeds", 40],
    ["grass", 20],
    ["tree_dead", 12],
    ["bush", 10],
    ["mushroom", 10],
    ["log", 8],
  ],
  rocky: [
    ["rock", 34],
    ["boulder", 22],
    ["cliff", 8],
    ["tree_pine", 10],
    ["tree_dead", 8],
    ["grass", 18],
  ],
  ashland: [
    ["tree_dead", 26],
    ["rock", 22],
    ["boulder", 8],
    ["bone_pile", 12],
    ["skull_pole", 4],
    ["grass", 20],
    ["mushroom", 8],
  ],
  graveyard: [
    ["tree_dead", 30],
    ["grass", 40],
    ["rock", 15],
    ["mushroom", 15],
  ],
  glacier: [
    ["tree_snowpine", 34],
    ["ice_spike", 14],
    ["ice_rock", 20],
    ["snowdrift", 22],
    ["boulder", 4],
    ["tree_dead", 6],
  ],
  lava: [
    ["basalt", 36],
    ["obsidian_spike", 18],
    ["lava_pool", 14],
    ["ember_vent", 14],
    ["tree_dead", 12],
    ["bone_pile", 6],
  ],
};

/** Minimum spacing for things with a trunk or bulk (grass and flowers ignore it). */
const SPACING: Record<string, number> = {
  skull_pole: 3,
  tree_oak: 3.4,
  tree_pine: 3,
  tree_birch: 2.6,
  tree_dead: 3,
  boulder: 3.5,
  cliff: 7,
  rock: 1.8,
  stump: 1.5,
  log: 3,
  haystack: 3,
  bush: 1.6,
  tree_snowpine: 3,
  ice_spike: 3,
  ice_rock: 2,
  basalt: 2.4,
  obsidian_spike: 3,
  lava_pool: 5,
};

const SCALE: Record<string, [number, number]> = {
  tree_oak: [0.8, 1.35],
  tree_pine: [0.8, 1.45],
  tree_birch: [0.85, 1.25],
  tree_dead: [0.8, 1.3],
  rock: [0.5, 1.4],
  boulder: [1, 2],
  cliff: [1, 1.8],
  bush: [0.7, 1.3],
  grass: [0.7, 1.3],
  flowers: [0.7, 1.2],
  fern: [0.7, 1.3],
  reeds: [0.8, 1.3],
  mushroom: [0.7, 1.4],
  tree_snowpine: [0.8, 1.4],
  ice_spike: [0.7, 1.6],
  ice_rock: [0.6, 1.4],
  snowdrift: [0.8, 1.5],
  basalt: [0.6, 1.5],
  obsidian_spike: [0.7, 1.6],
  lava_pool: [0.8, 1.6],
  ember_vent: [0.8, 1.2],
};

/** Things that grow in or right at the water's edge. */
const WATERSIDE = new Set(["reeds"]);

/**
 * Expands zone data into concrete placements. Pure and deterministic: the client builds meshes
 * from it and the server builds colliders from it, so both always agree.
 */
export function buildZoneLayout(zone: Zone): ZoneLayout {
  const town = zone.town;
  const structures: Placement[] = (town?.structures ?? []).map((s, i) => ({
    type: s.type,
    x: s.x,
    z: s.z,
    rot: s.rot * DEG,
    scale: 1,
    variant: i,
    ...(s.nation ? { nation: s.nation } : {}),
  }));

  const posts: ZoneLayout["posts"] = [];
  const gates: ZoneLayout["gates"] = [];
  if (town) {
    const { radius, gateAngles, gateWidth, postSpacing } = town.palisade;
    const cx = town.plaza.x;
    const cz = town.plaza.z;
    const gateHalf = gateWidth / 2 / radius;
    const steps = Math.floor((Math.PI * 2 * radius) / postSpacing);
    for (let i = 0; i < steps; i++) {
      const a = (i / steps) * Math.PI * 2;
      const inGate = gateAngles.some((g) => Math.abs(angleDiff(a, g * DEG)) < gateHalf);
      // A ruined wall has fallen in stretches (same pattern on server and client).
      const fallen = town.wall === "ruin" && Math.sin(a * 7) + Math.sin(a * 3 + 1) > 0.9;
      if (!inGate && !fallen)
        posts.push({ x: cx + Math.sin(a) * radius, z: cz + Math.cos(a) * radius });
    }
    for (const g of gateAngles)
      gates.push({
        x: cx + Math.sin(g * DEG) * radius,
        z: cz + Math.cos(g * DEG) * radius,
        rot: g * DEG,
      });
  }

  const nature = scatter(zone, structures);
  nature.push(...townGreenery(zone, structures, nature.length));
  return { structures, nature, posts, gates };
}

/** Inside the town wall: what grows on the free ground between the houses. */
const TOWN_MIX: [string, number][] = [
  ["tree_oak", 30],
  ["tree_birch", 18],
  ["bush", 24],
  ["flowers", 28],
];

/**
 * Trees, bushes and flowers on the town's free ground: kept off the plaza, the streets, the
 * gates, NPC stands and a margin around every building so doors and stalls stay open.
 */
function townGreenery(zone: Zone, structures: Placement[], firstVariant: number): Placement[] {
  const town = zone.town;
  const density = town?.greenery ?? 0;
  if (!town || density <= 0) return [];
  const rng = seededRng((zone.world?.seed ?? 1) + 7919);
  const inner = town.palisade.radius - 4;
  const npcs = zoneNpcs(zone.id);
  const out: Placement[] = [];
  const total = TOWN_MIX.reduce((s, [, w]) => s + w, 0);
  const count = Math.round((density * Math.PI * inner * inner) / 100);
  const free = (x: number, z: number, trunk: boolean) => {
    const d = Math.hypot(x - town.plaza.x, z - town.plaza.z);
    if (d > inner || d < town.plaza.radius + 3) return false;
    for (const p of town.paths)
      if (distToPolyline(x, z, p.points) < p.width / 2 + (trunk ? 2.2 : 1)) return false;
    for (const s of structures) {
      const def = getStructure(s.type);
      const reach = (Math.max(def.w, def.d) / 2) * s.scale + (trunk ? 2.6 : 1.2);
      if (Math.hypot(s.x - x, s.z - z) < reach) return false;
    }
    for (const n of npcs) if (Math.hypot(n.x - x, n.z - z) < 4) return false;
    for (const g of zone.portals ?? []) if (Math.hypot(g.x - x, g.z - z) < 7) return false;
    for (const o of out) {
      const gap = SPACING[o.type] && trunk ? Math.max(SPACING[o.type] ?? 0, 3.2) : 0.9;
      if (Math.hypot(o.x - x, o.z - z) < gap) return false;
    }
    return true;
  };
  // Each kind gets its own share, so small flowers do not use up the trees' places. Most
  // random spots land on a street or next to a house: keep trying until the shares are met.
  const quota = new Map(TOWN_MIX.map(([t, w]) => [t, Math.round((count * w) / total)]));
  for (let i = 0; i < count * 16 && out.length < count; i++) {
    const a = rng() * Math.PI * 2;
    const r = Math.sqrt(rng()) * inner;
    const x = town.plaza.x + Math.sin(a) * r;
    const z = town.plaza.z + Math.cos(a) * r;
    let roll = rng() * total;
    let type = TOWN_MIX[0]?.[0] as string;
    for (const [t, w] of TOWN_MIX) {
      roll -= w;
      if (roll <= 0) {
        type = t;
        break;
      }
    }
    if ((quota.get(type) ?? 0) <= 0 || !free(x, z, !!SPACING[type])) continue;
    quota.set(type, (quota.get(type) ?? 0) - 1);
    const [lo, hi] = SCALE[type] ?? [1, 1];
    out.push({
      type,
      x,
      z,
      rot: rng() * Math.PI * 2,
      scale: lo + rng() * (hi - lo),
      variant: firstVariant + out.length,
    });
  }
  return out;
}

function scatter(zone: Zone, structures: Placement[]): Placement[] {
  const world = zone.world;
  if (!world) return [];
  const rng = seededRng(world.seed);
  const half = zone.size / 2 - 3;
  const town = zone.town;
  const out: Placement[] = [];
  // Spatial hash of bulky things for spacing checks.
  const CELL = 4;
  const bulky = new Map<number, Placement[]>();
  const key = (cx: number, cz: number) => (cx + 1000) * 2048 + (cz + 1000);
  const crowded = (x: number, z: number, type: string) => {
    const s = SPACING[type];
    if (!s) return false;
    const cx = Math.floor(x / CELL);
    const cz = Math.floor(z / CELL);
    for (let i = -2; i <= 2; i++)
      for (let j = -2; j <= 2; j++) {
        for (const p of bulky.get(key(cx + i, cz + j)) ?? []) {
          const need = Math.max(s, SPACING[p.type] ?? 0) * 0.5 * (p.scale + 1);
          if (Math.hypot(p.x - x, p.z - z) < need) return true;
        }
      }
    return false;
  };
  const blocked = (x: number, z: number, type: string, clearance: number) => {
    if (Math.abs(x) > half || Math.abs(z) > half) return true;
    const wd = waterDistance(zone, x, z);
    // The bank dips under the surface until ~3.3 m from the water's edge.
    if (WATERSIDE.has(type) ? wd < -1 || wd > 4 : wd < 4) return true;
    if (town) {
      if (Math.hypot(x - town.plaza.x, z - town.plaza.z) < town.palisade.radius + 5) return true;
      for (const p of town.paths)
        if (distToPolyline(x, z, p.points) < p.width / 2 + clearance) return true;
    }
    for (const s of structures) if (Math.hypot(s.x - x, s.z - z) < 7) return true;
    for (const c of world.clearings) if (Math.hypot(c.x - x, c.z - z) < c.r) return true;
    return false;
  };
  const place = (type: string, x: number, z: number, rot?: number) => {
    const [a, b] = SCALE[type] ?? [1, 1];
    const p: Placement = {
      type,
      x,
      z,
      rot: rot ?? rng() * Math.PI * 2,
      scale: a + rng() * (b - a),
      variant: out.length,
    };
    out.push(p);
    if (SPACING[type]) {
      const k = key(Math.floor(x / CELL), Math.floor(z / CELL));
      const list = bulky.get(k) ?? [];
      list.push(p);
      bulky.set(k, list);
    }
  };

  world.regions.forEach((region, ri) => {
    const mix = BIOME_MIX[region.biome];
    const total = mix.reduce((s, [, w]) => s + w, 0);
    const count = Math.round((region.density * Math.PI * (region.r * 1.22) ** 2) / 100);
    for (let i = 0; i < count; i++) {
      const a = rng() * Math.PI * 2;
      // Sample a little wider than the radius; the ragged edge decides what is inside.
      const r = Math.sqrt(rng()) * region.r * 1.22;
      const x = region.x + Math.sin(a) * r;
      const z = region.z + Math.cos(a) * r;
      let roll = rng() * total;
      let type = mix[0]?.[0] as string;
      for (const [t, w] of mix) {
        roll -= w;
        if (roll <= 0) {
          type = t;
          break;
        }
      }
      if (regionReach(region, x, z, world.seed) > 1) continue;
      // A later region owns the ground it covers.
      if (world.regions.some((o, j) => j > ri && regionReach(o, x, z, world.seed) < 1)) continue;
      const trunk = !!SPACING[type];
      if (blocked(x, z, type, trunk ? 3 : 1)) continue;
      if (crowded(x, z, type)) continue;
      place(type, x, z);
    }
    if (region.biome === "graveyard") graves(region, place, blocked);
  });

  // Reeds along every shore.
  const shore = (x: number, z: number) => {
    if (rng() < 0.45 && !blocked(x, z, "reeds", 0.5)) place("reeds", x, z);
  };
  for (const l of world.water.lakes) {
    const n = Math.round((Math.PI * 2 * l.r) / 2.2);
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2;
      const r = l.r + (rng() - 0.35) * 3;
      shore(l.x + Math.sin(a) * r, l.z + Math.cos(a) * r);
    }
  }
  for (const river of world.water.rivers) {
    for (let i = 1; i < river.points.length; i++) {
      const [ax, az] = river.points[i - 1] as [number, number];
      const [bx, bz] = river.points[i] as [number, number];
      const len = Math.hypot(bx - ax, bz - az);
      const nx = -(bz - az) / len;
      const nz = (bx - ax) / len;
      for (let s = 0; s < len; s += 2.6) {
        const k = s / len;
        const side = rng() < 0.5 ? -1 : 1;
        const off = river.width / 2 + (rng() - 0.35) * 3;
        shore(ax + (bx - ax) * k + nx * off * side, az + (bz - az) * k + nz * off * side);
      }
    }
  }

  // Grass and flowers everywhere else, thinner than in the regions; none on snow or lava.
  const barren = (x: number, z: number) => {
    let biome: Biome | null = null;
    for (const r of world.regions) if (regionReach(r, x, z, world.seed) < 1) biome = r.biome;
    return biome === "glacier" || biome === "lava";
  };
  const cover = Math.round((world.groundCover * zone.size * zone.size) / 100);
  for (let i = 0; i < cover; i++) {
    const x = (rng() - 0.5) * zone.size;
    const z = (rng() - 0.5) * zone.size;
    const type = rng() < 0.72 ? "grass" : "flowers";
    if (!blocked(x, z, type, 0.8) && !barren(x, z)) place(type, x, z);
  }
  return out;
}

/** Rows of headstones facing south, with a few leaning or missing. */
function graves(
  region: { x: number; z: number; r: number },
  place: (type: string, x: number, z: number, rot?: number) => void,
  blocked: (x: number, z: number, type: string, clearance: number) => boolean,
) {
  const span = region.r * 0.6;
  for (let gz = -span; gz <= span; gz += 3.2) {
    for (let gx = -span; gx <= span; gx += 2.4) {
      const x = region.x + gx;
      const z = region.z + gz;
      if (Math.hypot(gx, gz) > span) continue;
      // Deterministic gaps without consuming the shared rng.
      if ((Math.floor(gx * 7 + gz * 13) & 7) === 0) continue;
      // Facing south, some leaning a little.
      const lean = (((gx * 31 + gz * 17) % 5) - 2) * 0.06;
      if (!blocked(x, z, "tombstone", 1)) place("tombstone", x, z, Math.PI + lean);
    }
  }
}

function angleDiff(a: number, b: number): number {
  let d = (a - b) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return d;
}

/** The name of where (x, z) is: the town inside its wall, else the named area, else the zone. */
export function areaName(zone: Zone, x: number, z: number): string {
  const town = zone.town;
  if (town && Math.hypot(x - town.plaza.x, z - town.plaza.z) < town.palisade.radius)
    return town.name;
  const regions = zone.world?.regions ?? [];
  for (let i = regions.length - 1; i >= 0; i--) {
    const r = regions[i];
    if (r?.name && regionReach(r, x, z, zone.world?.seed) < 1) return r.name;
  }
  return zone.name;
}

/** "Human homeland", "Orc homeland", "PvP zone" or "Neutral lands". */
export function realmName(zone: Zone): string {
  if (zone.pvp) return "PvP zone";
  return zone.nation === "human"
    ? "Human homeland"
    : zone.nation === "orc"
      ? "Orc homeland"
      : "Neutral lands";
}
